import { Injectable } from '@angular/core';

export interface PerformanceMetric {
  duration: number;
  timestamp: number;
  memoryUsage?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PerformanceMonitorService {
  private measurements = new Map<string, PerformanceMetric[]>();
  private activeMarks = new Set<string>();

  startMeasure(operationName: string) {
    if (this.activeMarks.has(operationName)) {
      console.warn(`Performance measurement '${operationName}' was already started`);
      return;
    }

    this.activeMarks.add(operationName);
    performance.mark(`${operationName}-start`);
  }

  endMeasure(operationName: string) {
    if (!this.activeMarks.has(operationName)) {
      console.warn(`No active measurement found for '${operationName}'`);
      return;
    }

    performance.mark(`${operationName}-end`);
    performance.measure(operationName, `${operationName}-start`, `${operationName}-end`);

    const measure = performance.getEntriesByName(operationName).pop();
    this.logMeasurement(operationName, measure.duration);

    // Cleanup
    this.activeMarks.delete(operationName);
    performance.clearMarks(`${operationName}-start`);
    performance.clearMarks(`${operationName}-end`);
    performance.clearMeasures(operationName);
  }

  private logMeasurement(operation: string, duration: number) {
    if (!this.measurements.has(operation)) {
      this.measurements.set(operation, []);
    }

    const metric: PerformanceMetric = {
      duration,
      timestamp: Date.now(),
      memoryUsage: (performance as any).memory?.usedJSHeapSize,
    };

    this.measurements.get(operation).push(metric);

    // Log to console in development
    console.log(`Performance [${operation}]: ${duration.toFixed(2)}ms`);
    if (duration > 100) {
      console.warn(`⚠️ Slow operation detected: ${operation} took ${duration.toFixed(2)}ms`);
    }
  }

  getMetrics() {
    const metrics: { [key: string]: any } = {};

    this.measurements.forEach((measurements, operation) => {
      const durations = measurements.map((m) => m.duration);
      metrics[operation] = {
        avg: this.calculateAverage(durations),
        min: Math.min(...durations),
        max: Math.max(...durations),
        count: measurements.length,
        total: durations.reduce((a, b) => a + b, 0),
      };
    });

    return metrics;
  }

  clearMetrics() {
    this.measurements.clear();
    this.activeMarks.clear();
    performance.clearMarks();
    performance.clearMeasures();
  }

  private calculateAverage(numbers: number[]): number {
    if (numbers.length === 0) return 0;
    const sum = numbers.reduce((a, b) => a + b, 0);
    return Number((sum / numbers.length).toFixed(2));
  }
}
